"use client"

import Image from "next/image"
import { Badge } from "@/components/ui/badge"

interface Partner {
  name: string
  logo: string
  category: "government" | "education" | "technology" | "foundation"
}

const partners: Partner[] = [
  {
    name: "University of Lagos",
    logo: "/disruptED26/partners/unilag.png",
    category: "education",
  },
  {
    name: "Federal Ministry of Education",
    logo: "/disruptED26/partners/federal-ministry-of-education.png",
    category: "government",
  },
  {
    name: "Ministry of Education",
    logo: "/disruptED26/partners/ministry.png",
    category: "government",
  },
  {
    name: "University of Birmingham",
    logo: "/disruptED26/partners/university-of-birmingham-seeklogo.png",
    category: "education",
  },
  {
    name: "Semicolon",
    logo: "/disruptED26/partners/semicolon.png",
    category: "technology",
  },
  {
    name: "Evolv Africa",
    logo: "/disruptED26/partners/evolv-a-logo.png",
    category: "foundation",
  },
]

const categoryLabels: Record<Partner["category"], string> = {
  government: "Government Partners",
  education: "Educational Institutions",
  technology: "Technology Partners",
  foundation: "Foundation & Social Impact",
}

export function DisruptEDPartnersSection() {
  const partnersByCategory = partners.reduce(
    (acc, partner) => {
      if (!acc[partner.category]) {
        acc[partner.category] = []
      }
      acc[partner.category].push(partner)
      return acc
    },
    {} as Record<Partner["category"], Partner[]>,
  )

  const renderPartnerCategory = (category: Partner["category"]) => {
    const categoryPartners = partnersByCategory[category]
    if (!categoryPartners || categoryPartners.length === 0) return null

    return (
      <div key={category} className="mb-20 last:mb-0">
        <h3 className="mb-10 text-2xl font-black text-foreground border-l-4 border-secondary pl-4 uppercase tracking-wider">
          {categoryLabels[category]}
        </h3>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {categoryPartners.map((partner) => (
            <div
              key={partner.name}
              className="group relative overflow-hidden rounded-2xl border border-border bg-slate-100/50 dark:bg-slate-800/50 p-8 transition-all duration-300 hover:shadow-2xl hover:border-secondary/30 hover:-translate-y-1"
            >
              <div className="relative h-32 w-full flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-500">
                <Image
                  src={partner.logo}
                  alt={partner.name}
                  width={200}
                  height={100}
                  className="object-contain max-h-24 max-w-full drop-shadow-sm"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg?height=100&width=200"
                  }}
                />
              </div>
              <div className="mt-6 pt-6 border-t border-border/50">
                <p className="text-center font-bold text-foreground text-sm uppercase tracking-tight">
                  {partner.name}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <section id="partners" className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="mx-auto mb-20 max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-secondary text-secondary font-bold px-4 py-1 uppercase tracking-[0.2em]">
            Collaboration
          </Badge>
          <h2 className="mb-6 text-4xl font-black text-foreground md:text-5xl lg:text-7xl tracking-tighter">
            Strategic <span className="text-secondary">Partners</span>
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed font-medium">
            DisruptED Summit 2026 is powered by a coalition of leading government bodies, academic institutions, and technology innovators across Africa.
          </p>
        </div>

        <div className="mx-auto">
          {(["government", "education", "technology", "foundation"] as const).map(
            (category) => renderPartnerCategory(category),
          )}
        </div>
      </div>
    </section>
  )
}
